import { Trash } from "@phosphor-icons/react";
import { useState } from "react";

import { INTEGRATIONS_MAP } from "features/integrations/constants";
import type { IntegrationSettings } from "features/integrations/types";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useIntegrationMutations from "../datahooks/useIntegrationMutations";

interface RemoveIntegrationButtonProps {
  integrationKey: keyof IntegrationSettings;
}

export default function RemoveIntegrationButton({
  integrationKey,
}: RemoveIntegrationButtonProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { removeIntegration, isRemovingIntegration } =
    useIntegrationMutations();

  function onRemove() {
    removeIntegration(integrationKey).then(() => setIsOpen(false));
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  const { title } = INTEGRATIONS_MAP[integrationKey];

  return (
    <>
      <Button
        variant={isDesktop ? "link-danger" : "ghost-danger"}
        size={isDesktop ? "sm" : "md"}
        className="self-end"
        leftIcon={<Trash />}
        onClick={() => setIsOpen(true)}
      >
        Remove {title}
      </Button>

      <Component open={isOpen} onOpenChange={setIsOpen}>
        <Content>
          <Header
            title="Remove integration?"
            subtitle={
              <>
                Are you sure you want to remove{" "}
                <span className="text-sm-bold text-black-950">{title}</span>{" "}
                integration from the workspace?
              </>
            }
          />

          <Footer className="items-center justify-end">
            <Close asChild>
              <Button
                variant="secondary-black"
                disabled={isRemovingIntegration}
              >
                Cancel
              </Button>
            </Close>
            <Button
              variant="outline-danger"
              onClick={onRemove}
              isLoading={isRemovingIntegration}
            >
              Remove
            </Button>
          </Footer>
        </Content>
      </Component>
    </>
  );
}
